import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { EmptyState } from "@/components/interactions/EmptyState";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { Bird, Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface BatchRow {
  id: string;
  placement_date: string;
  planned_cycle_days: number;
  batch_status: string;
  doc_count: number;
  cumulative_mortality: number | null;
  current_fcr: number | null;
  current_bird_count: number | null;
  batch_code: string | null;
}

interface DailyRecordRow {
  mortality_count: number;
  running_fcr: number | null;
}

type HealthStatus = "good" | "warning" | "critical";

const healthConfig: Record<HealthStatus, { emoji: string; labelKey: string; color: string }> = {
  good: { emoji: "🟢", labelKey: "cbs.batch.health.good", color: "text-success" },
  warning: { emoji: "🟡", labelKey: "cbs.batch.health.warning", color: "text-warning" },
  critical: { emoji: "🔴", labelKey: "cbs.batch.health.critical", color: "text-destructive" },
};

function getHealthStatus(fcr: number | null, mortalityPct: number): HealthStatus {
  if ((fcr !== null && fcr > 1.85) || mortalityPct > 5) return "critical";
  if ((fcr !== null && fcr >= 1.7) || mortalityPct >= 3) return "warning";
  return "good";
}

export const BatchStatusCard: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = getCurrentUserId();

  const { data: batch, isLoading: batchLoading } = useQuery<BatchRow | null>({
    queryKey: ["cbs", "active-batch", userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("batches")
        .select("id, placement_date, planned_cycle_days, batch_status, doc_count, cumulative_mortality, current_fcr, current_bird_count, batch_code")
        .eq("user_id", userId)
        .in("batch_status", ["placed", "active"])
        .order("placement_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const { data: todayRecord } = useQuery<DailyRecordRow | null>({
    queryKey: ["cbs", "today-record", batch?.id],
    queryFn: async () => {
      if (!batch) return null;
      const client = getAuthenticatedClient();
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await client
        .from("daily_records")
        .select("mortality_count, running_fcr")
        .eq("batch_id", batch.id)
        .eq("record_date", today)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!batch?.id,
  });

  const { data: creditScore } = useQuery<{ total_score: number } | null>({
    queryKey: ["cbs", "credit-score", userId],
    queryFn: async () => {
      if (!userId) return null;
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("credit_scores")
        .select("total_score")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  if (batchLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-6 bg-muted rounded w-1/2 mb-4" />
        <div className="h-4 bg-muted rounded w-full mb-2" />
        <div className="h-4 bg-muted rounded w-3/4" />
      </Card>
    );
  }

  if (!batch) {
    return (
      <Card className="p-6">
        <EmptyState
          icon={Bird}
          title={t("cbs.batch.noBatch", { defaultValue: "No Active Batch" })}
          description={t("cbs.batch.noBatchDesc", { defaultValue: "Start a new batch to begin tracking." })}
          action={{
            label: t("cbs.batch.new", { defaultValue: "Start New Batch" }),
            onClick: () => navigate("/batch/new"),
          }}
        />
      </Card>
    );
  }

  // Calculate batch day
  const placementDate = new Date(batch.placement_date);
  const today = new Date();
  const diffMs = today.getTime() - placementDate.getTime();
  const currentDay = Math.max(1, Math.floor(diffMs / 86400000) + 1);
  const targetDays = batch.planned_cycle_days;
  const progressPct = Math.min(100, (currentDay / targetDays) * 100);

  // Mortality percentage
  const cumulativeMortality = batch.cumulative_mortality ?? 0;
  const mortalityPct = batch.doc_count > 0 ? (cumulativeMortality / batch.doc_count) * 100 : 0;

  // FCR — prefer today's running FCR, fall back to batch-level
  const fcr = todayRecord?.running_fcr ?? batch.current_fcr;

  const health = getHealthStatus(fcr, mortalityPct);
  const { emoji, labelKey, color } = healthConfig[health];

  const todayMortality = todayRecord?.mortality_count ?? 0;

  return (
    <Card className="p-5 space-y-4" onClick={() => navigate(`/batch/${batch.id}`)} interactive>
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bird className="w-5 h-5 text-primary" />
          <h3 className="font-semibold text-foreground">
            {batch.batch_code || t("cbs.batch.title", { defaultValue: "Active Batch" })}
          </h3>
        </div>
        <motion.span
          animate={health !== 'good' ? { scale: [1, 1.1, 1] } : undefined}
          transition={health !== 'good' ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : undefined}
          className={cn("text-sm font-medium flex items-center gap-1", color)}
        >
          {emoji} {t(labelKey, { defaultValue: labelKey.split(".").pop() ?? "" })}
        </motion.span>
      </div>

      {/* Day progress */}
      <div className="space-y-1.5">
        <div className="flex justify-between text-sm">
          <span className="font-semibold text-foreground">
            {t("cbs.batch.day", { defaultValue: "Day" })} {Math.min(currentDay, targetDays)}{" "}
            <span className="font-normal text-muted-foreground">
              {t("common.of", { defaultValue: "of" })} {targetDays}
            </span>
          </span>
          
        </div>
        <Progress value={progressPct} className="h-2" />
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="rounded-xl bg-muted/50 p-2.5">
          {/* FCR (Feed Conversion Ratio) — kept untranslated; universal poultry-feed acronym in Lao trade. */}
          <div className="text-xs text-muted-foreground mb-0.5">FCR</div>
          <div className="text-lg font-bold text-foreground">
            {fcr !== null && fcr !== undefined ? fcr.toFixed(2) : "—"}
          </div>
        </div>
        <div className="rounded-xl bg-muted/50 p-2.5">
          <div className="text-xs text-muted-foreground mb-0.5">
            {t("cbs.mortality.today", { defaultValue: "Today ☠️" })}
          </div>
          <div className="text-lg font-bold text-foreground">{todayMortality}</div>
        </div>
        <div className="rounded-xl bg-primary/10 p-2.5 cursor-pointer" onClick={(e) => { e.stopPropagation(); }}>
          <div className="text-xs text-muted-foreground mb-0.5 flex items-center justify-center gap-0.5">
            <Star className="w-3 h-3" /> {t("nav.score", { defaultValue: "Score" })}
          </div>
          <div className="text-lg font-bold text-primary">{creditScore?.total_score ?? "—"}</div>
        </div>
      </div>

    </Card>
  );
};
